import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, Chapter } from "./Reveal";

const SINGLES = {
  plate: { tagline: "A flat NFC review plate for counters, tables and desks.", img: "/assets/hero-plate.webp", alt: "Revora NFC Review Plate — white plate with Google review branding" },
  card: { tagline: "A handheld NFC review card your team can carry anywhere.", img: "/assets/hero-card.webp", alt: "Revora handheld NFC review card with Google review branding" },
  set: { tagline: "One stand, one plate and one handheld card — with basic tap-to-review and setup instructions.", img: "/assets/hero-set.webp", alt: "Revora Complete Set — NFC stand, review plate and handheld card" },
};

export const Pricing = () => {
  const [packages, setPackages] = useState([]);
  const [standQty, setStandQty] = useState("stand-10");
  const [singleQty, setSingleQty] = useState({ plate: "plate-1", card: "card-1" });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data.packages)).catch(() => {});
  }, []);

  const stands = useMemo(() => packages.filter((p) => p.product === "stand").sort((a, b) => a.quantity - b.quantity), [packages]);
  const singles = useMemo(
    () =>
      ["plate", "card", "set"]
        .map((prod) => ({
          product: prod,
          tiers: packages.filter((p) => p.product === prod).sort((a, b) => a.quantity - b.quantity),
        }))
        .filter((g) => g.tiers.length > 0),
    [packages]
  );
  const activeStand = stands.find((p) => p.id === standQty) || stands[0];

  return (
    <section id="products" className="bg-white py-28 sm:py-36" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6">
        <Chapter number="04" label="Products" />
        <Reveal>
          <h2 className="rvr-title font-display text-4xl md:text-5xl font-bold tracking-tight text-ink leading-[1.05] max-w-3xl">
            Revora tap-to-review products
          </h2>
          <p className="mt-5 text-black/55 max-w-2xl leading-relaxed">
            Every Revora review product includes an NFC chip, destination setup, and shipping.
            During checkout, provide the review URL you want customers to open. Starter includes a fixed
            destination; Essential and Growth allow you to update destinations later without replacing the product.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-5">
          <Reveal className="lg:col-span-7">
            <div className="border border-black/10 rounded-sm p-8 h-full flex flex-col sm:flex-row gap-8" data-testid="product-stand">
              <div className="w-44 shrink-0 mx-auto sm:mx-0 rounded-sm overflow-hidden">
                <img
                  src="/assets/hero-stand.webp"
                  alt="Revora NFC Stand — white freestanding stand with Google review branding"
                  className="w-full h-auto block"
                />
              </div>
              <div className="flex-1">
                <p className="font-display text-2xl font-bold text-ink">Revora NFC Review Stand</p>
                <p className="text-sm text-black/55 mt-1 mb-6">The freestanding countertop stand. Choose your quantity:</p>
                <div className="flex flex-wrap gap-2 mb-6" data-testid="stand-quantity-selector">
                  {stands.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`stand-qty-${p.quantity}`}
                      onClick={() => setStandQty(p.id)}
                      className={`relative px-4 py-2.5 rounded-full text-sm font-bold border transition-colors duration-200 ${
                        standQty === p.id ? "bg-ink text-white border-ink" : "border-black/15 text-ink hover:border-ink"
                      }`}
                    >
                      {p.quantity}
                      {p.featured && standQty !== p.id && (
                        <span className="absolute -top-2 -right-1 w-2.5 h-2.5 rounded-full bg-volt" />
                      )}
                    </button>
                  ))}
                </div>
                {activeStand && (
                  <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-display text-4xl font-black text-ink" data-testid="stand-price">${activeStand.price.toFixed(2)}</p>
                      <p className="text-xs text-black/45 mt-1">
                        {activeStand.quantity === 1 ? "1 stand" : `${activeStand.quantity} stands`}
                        {activeStand.featured && <span className="ml-2 text-voltdark font-bold">Most popular</span>}
                      </p>
                    </div>
                    <button
                      data-testid="order-stand-button"
                      onClick={() => navigate(`/order?pkg=${activeStand.id}`)}
                      className="group inline-flex items-center gap-2 bg-volt text-ink font-bold px-7 py-3.5 rounded-full hover:bg-voltdark hover:text-white transition-colors duration-300"
                    >
                      Configure & Order <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 grid gap-5">
            {singles.map((g, i) => {
              const active = g.tiers.find((t) => t.id === singleQty[g.product]) || g.tiers[0];
              return (
                <Reveal key={g.product} delay={0.08 * (i + 1)}>
                  <div className="border border-black/10 rounded-sm p-6 flex items-center gap-6 hover:border-ink transition-colors duration-300" data-testid={`product-${g.product}`}>
                    <div className="w-20 h-20 shrink-0 rounded-sm overflow-hidden">
                      <img src={SINGLES[g.product]?.img} alt={SINGLES[g.product]?.alt || ""} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-lg font-bold text-ink">{g.tiers[0].name}</p>
                      <p className="text-xs text-black/55 mt-0.5">{SINGLES[g.product]?.tagline}</p>
                      {g.tiers.length > 1 && (
                        <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`${g.product}-quantity-selector`}>
                          {g.tiers.map((t) => (
                            <button
                              key={t.id}
                              data-testid={`${g.product}-qty-${t.quantity}`}
                              onClick={() => setSingleQty((s) => ({ ...s, [g.product]: t.id }))}
                              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors duration-200 ${
                                active.id === t.id ? "bg-ink text-white border-ink" : "border-black/15 text-ink hover:border-ink"
                              }`}
                            >
                              {t.quantity}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <p className="font-display text-xl font-black text-ink" data-testid={`${g.product}-price`}>
                          ${active.price.toFixed(2)}
                          {active.quantity > 1 && (
                            <span className="text-[11px] font-normal text-black/40 ml-1.5">
                              {active.quantity} {g.product === "card" ? "cards" : g.product === "plate" ? "plates" : "sets"}
                            </span>
                          )}
                        </p>
                        <button
                          data-testid={`order-${g.product}-button`}
                          onClick={() => navigate(`/order?pkg=${active.id}`)}
                          className="inline-flex items-center gap-1.5 text-sm font-bold text-voltdark hover:text-ink transition-colors"
                        >
                          Order <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.15}>
          <p className="mt-10 text-xs text-black/45 flex items-center gap-2" data-testid="plan-notice">
            Plans add destination editing and tap analytics in the Revora business portal — details in the Portal section below. Every product routes to your review link regardless of plan.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
